import { useState, useEffect } from "react";
import { Navigate } from "react-router";
import { useAuth } from "../../hooks/useAuth";
import {
  getBusinessBookings,
  cancelBooking,
  completeBooking,
  markNoShow,
} from "../../api/booking.api";
import { apiClient } from "../../api/client";
import { Skeleton, SkeletonText } from "../../components/ui/Skeleton";
import { EmptyState } from "../../components/ui/EmptyState";
import type { Booking, BusinessProfile } from "@abejon/shared";

type DashboardBooking = Booking & { serviceName: string };

function formatTime(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour}:${m.toString().padStart(2, "0")} ${period}`;
}

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hrs}h ${mins}min` : `${hrs}h`;
}

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateLabel(dateStr: string): string {
  const today = todayStr();
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  const label = date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  if (dateStr === today) return `Today — ${label}`;
  return label;
}

const STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  confirmed: { bg: "bg-blue-50", text: "text-blue-700", label: "Confirmed" },
  completed: { bg: "bg-green-50", text: "text-green-700", label: "Completed" },
  cancelled_by_business: { bg: "bg-red-50", text: "text-red-700", label: "Cancelled" },
  cancelled_by_customer: { bg: "bg-orange-50", text: "text-orange-700", label: "Cancelled by client" },
  no_show: { bg: "bg-gray-100", text: "text-gray-600", label: "No-show" },
};

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "confirmed", label: "Confirmed" },
  { value: "completed", label: "Completed" },
  { value: "cancelled_by_business", label: "Cancelled" },
  { value: "cancelled_by_customer", label: "Cancelled by client" },
  { value: "no_show", label: "No-show" },
];

export function BookingsPage() {
  const { user, isLoading, isAuthenticated } = useAuth();
  const [bookingsList, setBookingsList] = useState<DashboardBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(todayStr());
  const [statusFilter, setStatusFilter] = useState("");
  const [message, setMessage] = useState("");
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [hasBusinessProfile, setHasBusinessProfile] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [loadKey, setLoadKey] = useState(0);

  useEffect(() => {
    if (!user || user.accountType !== "business") return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setFetchError(false);
      try {
        // Verify business profile exists
        const data = await apiClient<{ businesses: BusinessProfile[] }>(
          "/businesses?mine=true"
        ).catch(() => null);

        if (cancelled) return;

        if (!data?.businesses?.[0]) {
          setHasBusinessProfile(false);
          setLoading(false);
          return;
        }

        const result = await getBusinessBookings({
          date: date || undefined,
          status: statusFilter || undefined,
        });
        if (!cancelled) setBookingsList(result.bookings);
      } catch {
        if (!cancelled) setFetchError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user, date, statusFilter, loadKey]);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl py-8 px-4">
        <SkeletonText className="mb-2 h-7 w-32" />
        <SkeletonText className="mb-6 h-4 w-48" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.accountType !== "business") {
    return <Navigate to="/" replace />;
  }

  if (!loading && !hasBusinessProfile) {
    return (
      <div className="py-16 text-center">
        <h1 className="text-xl font-bold text-gray-900">No Business Profile</h1>
        <p className="mt-2 text-sm text-gray-500">
          Create a business profile first in Settings.
        </p>
      </div>
    );
  }

  async function handleCancel(booking: DashboardBooking) {
    const reason = prompt("Cancel reason (optional):");
    if (reason === null) return; // user clicked Cancel on prompt

    setActionLoading(booking.id);
    setMessage("");
    try {
      const { booking: updated } = await cancelBooking(booking.id);
      setBookingsList((prev) =>
        prev.map((b) => (b.id === updated.id ? { ...b, ...updated } : b))
      );
      setMessage("Booking cancelled");
    } catch {
      setMessage("Failed to cancel booking");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleComplete(booking: DashboardBooking) {
    setActionLoading(booking.id);
    setMessage("");
    try {
      const { booking: updated } = await completeBooking(booking.id);
      setBookingsList((prev) =>
        prev.map((b) => (b.id === updated.id ? { ...b, ...updated } : b))
      );
      setMessage("Booking marked as completed");
    } catch {
      setMessage("Failed to complete booking");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleNoShow(booking: DashboardBooking) {
    if (!confirm("Mark this booking as a no-show?")) return;

    setActionLoading(booking.id);
    setMessage("");
    try {
      const { booking: updated } = await markNoShow(booking.id);
      setBookingsList((prev) =>
        prev.map((b) => (b.id === updated.id ? { ...b, ...updated } : b))
      );
      setMessage("Booking marked as no-show");
    } catch {
      setMessage("Failed to mark as no-show");
    } finally {
      setActionLoading(null);
    }
  }

  function getClientName(booking: DashboardBooking): string {
    if (booking.guestName) return booking.guestName;
    return "Registered user";
  }

  function getClientContact(booking: DashboardBooking): string | null {
    if (booking.guestEmail) return booking.guestEmail;
    if (booking.guestPhone) return booking.guestPhone;
    return null;
  }

  const confirmedCount = bookingsList.filter((b) => b.status === "confirmed").length;

  return (
    <div className="mx-auto max-w-2xl py-8 px-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Bookings</h1>
        <p className="mt-1 text-sm text-gray-500">
          {date && formatDateLabel(date)}
          {confirmedCount > 0 && ` — ${confirmedCount} confirmed`}
        </p>
      </div>

      {message && (
        <p className={`mb-4 text-sm ${message.includes("Failed") ? "text-red-600" : "text-green-600"}`}>
          {message}
        </p>
      )}

      {/* Filters */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        {date && (
          <button
            onClick={() => setDate("")}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Show all dates
          </button>
        )}
      </div>

      {fetchError ? (
        <div className="py-12 text-center">
          <p className="mb-3 text-sm text-gray-500">Failed to load bookings.</p>
          <button
            onClick={() => setLoadKey((k) => k + 1)}
            className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
          >
            Retry
          </button>
        </div>
      ) : loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-lg border border-gray-200 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 space-y-2">
                  <SkeletonText className="h-4 w-40" />
                  <SkeletonText className="h-4 w-28" />
                  <SkeletonText className="h-3 w-36" />
                  <SkeletonText className="h-3 w-24" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Skeleton className="h-7 w-20 rounded-md" />
                  <Skeleton className="h-7 w-20 rounded-md" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : bookingsList.length === 0 ? (
        <EmptyState
          title="No bookings yet"
          description={date
            ? "No bookings for this date."
            : "Bookings will appear here once customers start booking."}
        />
      ) : (
        <div className="space-y-3">
          {bookingsList.map((booking) => {
            const style = STATUS_STYLES[booking.status] ?? STATUS_STYLES.confirmed;
            const isConfirmed = booking.status === "confirmed";
            const isActioning = actionLoading === booking.id;
            const contact = getClientContact(booking);

            return (
              <div
                key={booking.id}
                className="rounded-lg border border-gray-200 p-4"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    {/* Time + Service */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">
                        {formatTime(booking.startTime)} — {formatTime(booking.endTime)}
                      </span>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${style.bg} ${style.text}`}>
                        {style.label}
                      </span>
                    </div>

                    {/* Service name */}
                    <p className="mt-1 text-sm font-medium text-gray-700">
                      {booking.serviceName}
                    </p>

                    {/* Client info */}
                    <p className="mt-0.5 text-sm text-gray-500">
                      {getClientName(booking)}
                      {contact && (
                        <span className="ml-1 text-gray-400">· {contact}</span>
                      )}
                    </p>

                    {/* Duration + Price */}
                    <p className="mt-1 text-xs text-gray-400">
                      {formatDuration(booking.durationMinutes)} · {formatPrice(booking.priceInCents)}
                    </p>

                    {booking.notes && (
                      <p className="mt-1 text-xs text-gray-400 italic">
                        {booking.notes}
                      </p>
                    )}
                  </div>

                  {/* Actions for confirmed bookings */}
                  {isConfirmed && (
                    <div className="flex flex-wrap gap-1.5 sm:flex-col">
                      <button
                        onClick={() => handleComplete(booking)}
                        disabled={isActioning}
                        aria-label={`Mark ${getClientName(booking)}'s booking as complete`}
                        className="rounded-md bg-green-50 px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-100 disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
                      >
                        Complete
                      </button>
                      <button
                        onClick={() => handleNoShow(booking)}
                        disabled={isActioning}
                        aria-label={`Mark ${getClientName(booking)}'s booking as no-show`}
                        className="rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-200 disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
                      >
                        No-show
                      </button>
                      <button
                        onClick={() => handleCancel(booking)}
                        disabled={isActioning}
                        aria-label={`Cancel ${getClientName(booking)}'s booking`}
                        className="rounded-md bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
